import React from "react";
import { observer } from "mobx-react";
import { Dialog, DialogContent, DialogActions, Button } from "@mui/material";
import { useRootStore } from "../../../store/contexts";
import { RootStore } from "../../../store";

type CreateEntityDialogProps = {
  open: boolean;
  initialValue: string;
  onCreated: (value: { displayId: string; id: string }) => void;
  onCancelled: () => void;
  createStore: (rootStore: RootStore) => any;
  renderForm: (store: any) => React.ReactNode;
  saveMethod: string;
  cancelMethod: string;
};

export const CreateEntityDialog: React.FC<CreateEntityDialogProps> = observer(
  ({
    open,
    initialValue,
    onCreated,
    onCancelled,
    createStore,
    renderForm,
    saveMethod,
    cancelMethod,
  }) => {
    const rootStore = useRootStore();
    const storeRef = React.useRef<any>(null);
    const initializedForValue = React.useRef<string | null>(null);
    const [storeReady, setStoreReady] = React.useState(false);

    React.useEffect(() => {
      if (open) {
        if (!storeRef.current) {
          const store = createStore(rootStore);
          store.closeHandler = (returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) => {
            if (returnData && !returnData.cancelled && (returnData.resultId || returnData.entity)) {
              const displayId = returnData.entity?.displayId || String(returnData.resultId);
              const id = returnData.entity?.id || returnData.resultId || "";
              onCreated({ displayId, id });
            } else {
              onCancelled();
            }
            if (storeRef.current?.destroy) {
              storeRef.current.destroy();
            }
            storeRef.current = null;
            initializedForValue.current = null;
            setStoreReady(false);
          };
          storeRef.current = store;
          setStoreReady(true);
        }

        if (storeRef.current && initializedForValue.current !== initialValue) {
          initializedForValue.current = initialValue;
          storeRef.current.initialize(
            initialValue ? { initialDisplayId: initialValue } : undefined
          );
        }
      }

      return () => {
        if (!open && storeRef.current) {
          if (storeRef.current.destroy) {
            storeRef.current.destroy();
          }
          storeRef.current = null;
          initializedForValue.current = null;
          setStoreReady(false);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- lifecycle driven by open/initialValue only; createStore/onCreated/onCancelled/rootStore are reference-unstable props that would over-trigger
    }, [open, initialValue]);

    if (!open || !storeReady) return null;

    const store = storeRef.current;
    // El closeHandler nulea el ref ANTES de que setStoreReady(false) se
    // aplique: una re-renderización de mobx en esa ventana llegaba acá con
    // store null y explotaba en store.status.
    if (!store) return null;

    return (
      <Dialog
        open={true}
        fullWidth
        maxWidth="md"
        // Encima de los modales de formulario (10200) — sin esto, el "Agregar
        // <nuevo>" desde un dropdown dentro de un modal abría DETRÁS (solo se
        // veía oscurecerse el fondo). Los poppers de dropdowns van a 10300
        // para funcionar también dentro de este diálogo.
        sx={{ zIndex: 10250 }}
        onClose={() => {
          if (store[cancelMethod]) {
            store[cancelMethod]();
          } else {
            onCancelled();
          }
        }}
      >
        <DialogActions style={{ justifyContent: "flex-start" }}>
          <Button
            onClick={() => store[saveMethod]?.()}
            color="primary"
            disabled={store.status === "processing"}
          >
            {rootStore.langStore.saveNewEntity}
          </Button>
          <Button
            onClick={() => {
              if (store[cancelMethod]) {
                store[cancelMethod]();
              } else {
                onCancelled();
              }
            }}
            color="secondary"
          >
            {rootStore.langStore.cancelNewEntity}
          </Button>
        </DialogActions>
        <DialogContent>{renderForm(store)}</DialogContent>
      </Dialog>
    );
  }
);
